/* woolberry: shipping/reassurance bars — shared runtime for the PDP bar
   (.wb-shipbar) and the cart drawer bar (.wb-cartbar).
   ---------------------------------------------------------------------------
   Two jobs:

   1) "Ships by: <next business day>" — filled into every [data-wb-shipby].
      The 3PL ships from Sweden, so "today" is Sweden's calendar date
      (Europe/Stockholm) and non-shipping days are Swedish weekends + public
      holidays (röda dagar) + the "afton" days a warehouse is usually closed.
      Lives here (site-wide, deferred) rather than in the PDP snippet because
      the cart drawer renders on every page and needs it too.

   2) Fit-to-one-line — each bar's text is sized AS LARGE AS it can be while
      the whole centered row still fits on one line, measured live so it
      adapts to the exact device width AND the actual text length (short
      "Tomorrow" gets bigger than a long weekday). Sets --wb-fit on the bar;
      the CSS reads it (with a clamp() fallback if this script never runs). */
(function () {
  'use strict';

  /* ---------- 1. Ships-by date ---------- */

  function key(d) {
    return d.getUTCFullYear() + '-' + (d.getUTCMonth() + 1) + '-' + d.getUTCDate();
  }
  function addDays(d, n) {
    var x = new Date(d);
    x.setUTCDate(x.getUTCDate() + n);
    return x;
  }
  function stockholmToday() {
    var p = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Stockholm',
      year: 'numeric', month: '2-digit', day: '2-digit'
    }).format(new Date()).split('-');
    return new Date(Date.UTC(+p[0], +p[1] - 1, +p[2]));
  }
  function holidaysFor(year) {
    var a = year % 19, b = Math.floor(year / 100), c = year % 100,
        d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25),
        g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30,
        i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7,
        m = Math.floor((a + 11 * h + 22 * l) / 451),
        month = Math.floor((h + l - 7 * m + 114) / 31),
        day = ((h + l - 7 * m + 114) % 31) + 1;
    var easter = new Date(Date.UTC(year, month - 1, day));
    var set = {};
    function mark(dt) { set[key(dt)] = true; }
    mark(new Date(Date.UTC(year, 0, 1)));   // Nyårsdagen
    mark(new Date(Date.UTC(year, 0, 6)));   // Trettondedag jul
    mark(new Date(Date.UTC(year, 4, 1)));   // Första maj
    mark(new Date(Date.UTC(year, 5, 6)));   // Nationaldagen
    mark(new Date(Date.UTC(year, 11, 24))); // Julafton
    mark(new Date(Date.UTC(year, 11, 25))); // Juldagen
    mark(new Date(Date.UTC(year, 11, 26))); // Annandag jul
    mark(new Date(Date.UTC(year, 11, 31))); // Nyårsafton
    mark(addDays(easter, -2)); // Långfredagen
    mark(addDays(easter, 1));  // Annandag påsk
    mark(addDays(easter, 39)); // Kristi himmelsfärdsdag
    for (var dd = 19; dd <= 25; dd++) { // Midsommarafton (the Friday)
      var x = new Date(Date.UTC(year, 5, dd));
      if (x.getUTCDay() === 5) { mark(x); break; }
    }
    return set;
  }
  var cache = {};
  function isClosed(d) {
    var wd = d.getUTCDay();
    if (wd === 0 || wd === 6) return true;
    var y = d.getUTCFullYear();
    if (!cache[y]) cache[y] = holidaysFor(y);
    return !!cache[y][key(d)];
  }
  function shipByLabel() {
    var ship = stockholmToday(), diff = 0;
    do { ship = addDays(ship, 1); diff++; } while (isClosed(ship));
    // UK-native date, e.g. "24 Jul", always appended after the relative word
    var date = ship.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
    if (diff === 1) return 'Tomorrow, ' + date;
    if (diff <= 6) return ship.toLocaleDateString('en-GB', { weekday: 'long', timeZone: 'UTC' }) + ', ' + date;
    // rare >6-day gap (Christmas): the date alone (a far weekday reads oddly)
    return date;
  }
  function fillShipBy() {
    var text = shipByLabel();
    document.querySelectorAll('[data-wb-shipby]').forEach(function (el) {
      if (el.textContent !== text) el.textContent = text;
    });
  }

  /* ---------- 2. Fit to one line ---------- */

  // The cells are pushed to opposite edges (justify-content: space-between),
  // so "fits" means their COMBINED content width plus a minimum middle gap
  // stays within the bar — not the span (which is always the full bar width
  // once they're edge-aligned).
  var MIN_GAP = 16;
  function contentFits(bar) {
    var cells = bar.querySelectorAll('.wb-shipbar__cell, .wb-cartbar__cell');
    if (!cells.length) return true;
    var sum = 0;
    cells.forEach(function (c) { sum += c.getBoundingClientRect().width; });
    // available = the bar's content box (it's padded to sit inside the pill)
    var cs = getComputedStyle(bar);
    var avail = bar.clientWidth - parseFloat(cs.paddingLeft || 0) - parseFloat(cs.paddingRight || 0);
    return sum + MIN_GAP <= avail + 0.5;
  }

  // Target size = the product page's "Color" / "Size" labels (the theme's
  // body-x-small: 12px desktop / 12.8px mobile). The bars render AT this size
  // wherever it fits one line, and only shrink below it on the narrowest
  // phones — so they match the labels everywhere there's room.
  function targetSize() {
    var root = getComputedStyle(document.documentElement);
    var v = root.getPropertyValue('--font-body-x-small').trim();
    var px = parseFloat(v);
    if (v.indexOf('rem') !== -1) px *= parseFloat(root.fontSize) || 16;
    return px || 12.8;
  }

  // Largest font in [minF, maxF] that keeps the row on one line.
  function fit(bar, minF, maxF) {
    if (!bar || !bar.clientWidth) return;
    bar.style.setProperty('--wb-fit', maxF + 'px');
    if (contentFits(bar)) return; // the target size fits — use it as-is
    var lo = minF, hi = maxF;
    for (var i = 0; i < 8; i++) {
      var mid = (lo + hi) / 2;
      bar.style.setProperty('--wb-fit', mid + 'px');
      if (contentFits(bar)) lo = mid; else hi = mid;
    }
    bar.style.setProperty('--wb-fit', lo.toFixed(2) + 'px');
  }

  function fitAll() {
    var cap = targetSize();
    // PDP bar: no longer fitted. It stacks on two lines on mobile (see
    // assets/wb-product.css) and sits side-by-side on desktop, so it can use
    // a normal readable size instead of being shrunk to hold one line.
    document.querySelectorAll('.wb-shipbar').forEach(function (b) {
      b.style.removeProperty('--wb-fit');
    });
    // cart bar: the drawer is always narrow, so keep fitting it to one line
    document.querySelectorAll('.wb-cartbar').forEach(function (b) {
      fit(b, 7, cap);
    });
  }

  /* ---------- 3. Out-of-stock state on the PDP bar ----------
     Selecting a sold-out size used to leave the bar promising "Dispatched:
     Tomorrow" behind a green pulsing dot, which is the opposite of true.

     The signal is <script data-selected-variant>, the JSON blob the variant
     picker rewrites on every change. Verified against the live PDP: picking XL
     updates it to {"title":"Black / XL","available":false} while the bar sat
     unchanged. It is a better source than the Add to cart button's label,
     which is localised, and better than the pub/sub event, whose payload shape
     is not guaranteed by the theme.

     Only the DISPATCH cell changes (the one holding [data-wb-shipby]). The
     other cell carries delivery and duties copy, which is still true for a
     size that is merely out of stock right now.

     The original markup is cached on first change so reselecting an available
     size restores the real date rather than a rebuilt guess. */
  /* Two reds, on purpose.

     The DOT is a lighter red so the pulse reads softer. The LABEL stays on the
     site's pomegranate, because lightening 12px text hurts legibility on the
     cream background: measured against #fcfbf9, #c0392b gives 5.26:1 (passes
     WCAG AA for normal text) while #d24a3d drops to 4.24:1 (fails the 4.5:1
     threshold). The dot is a decorative indicator with aria-hidden, so it
     carries no contrast requirement and can be as light as it likes. */
  var OOS_DOT = '#d24a3d';  // lighter red — same hue as the pomegranate, ~6% lighter
  var OOS_TEXT = '#c0392b'; // the site's pomegranate, kept for readable text

  function dispatchCell(bar) {
    var el = bar.querySelector('[data-wb-shipby]');
    return el ? el.closest('.wb-shipbar__cell') : null;
  }

  function selectedAvailable() {
    var el = document.querySelector('script[data-selected-variant]');
    if (!el) return null; // no picker on this page: leave the bar alone
    try {
      var v = JSON.parse(el.textContent);
      return v && typeof v.available === 'boolean' ? v.available : null;
    } catch (e) {
      return null;
    }
  }

  function applyStock() {
    var available = selectedAvailable();
    if (available === null) return;

    document.querySelectorAll('.wb-shipbar').forEach(function (bar) {
      var cell = dispatchCell(bar) || bar.__wbOosCell;
      if (!cell) return;

      if (!available) {
        if (!bar.__wbOosCell) {
          bar.__wbOosCell = cell;
          bar.__wbOosHTML = cell.innerHTML;
        }
        var dot = cell.querySelector('.wb-shipbar__dot');
        var txt = cell.querySelector('.wb-shipbar__text');
        if (dot) dot.style.setProperty('--wb-sb-dot', OOS_DOT);
        if (txt) txt.textContent = 'Out of stock';
        cell.style.color = OOS_TEXT;
        bar.classList.add('wb-shipbar--oos');
      } else if (bar.__wbOosHTML != null) {
        cell.innerHTML = bar.__wbOosHTML;
        cell.style.removeProperty('color');
        bar.classList.remove('wb-shipbar--oos');
        bar.__wbOosCell = null;
        bar.__wbOosHTML = null;
        fillShipBy(); // the restored markup has the placeholder text again
      }
    });
  }

  /* Triggering this is the fiddly part. Observing <script data-selected-variant>
     itself does NOT work: on a variant change Broadcast re-renders the section
     and REPLACES that node, so an observer bound to it is watching a detached
     element and never fires again (this was the first attempt, and the bar sat
     green on a sold-out XL). So watch a stable ancestor instead, and re-read
     the JSON fresh on every call. */
  var stockT;
  function queueStock() {
    clearTimeout(stockT);
    stockT = setTimeout(applyStock, 60);
  }

  function watchVariant() {
    var bar = document.querySelector('.wb-shipbar');
    if (!bar) return;
    var host = bar.closest('[id^="shopify-section"]') || document.body;
    if (host.__wbStockWatched) return;
    host.__wbStockWatched = true;
    new MutationObserver(queueStock).observe(host, { childList: true, subtree: true });
  }

  // fast path: the radios bubble a change before the re-render lands, and the
  // re-render itself is caught by the observer above
  document.addEventListener('change', function (e) {
    if (e.target && e.target.closest && e.target.closest('variant-selects, .product__block--variants')) {
      queueStock();
    }
  });

  function run() { fillShipBy(); fitAll(); watchVariant(); applyStock(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  window.addEventListener('load', run); // re-fit once fonts/flag images settle

  var rt;
  window.addEventListener('resize', function () {
    clearTimeout(rt);
    rt = setTimeout(fitAll, 120);
  });

  document.addEventListener('shopify:section:load', run);

  // the cart bar is off-screen until the drawer opens — re-fit when it does
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-cart-toggle], [data-drawer-toggle]')) {
      setTimeout(run, 350);
    }
  });
})();
